/**
 * Точка входа: инициализация Pixi, создание сцены, запуск игры
 */

import * as PIXI from 'pixi.js';
import { TextStyle } from 'pixi.js';
import { Game } from './game/Game';
import { Header } from './ui/Header';
import { Footer } from './ui/Footer';
import { BetControl } from './ui/BetControl';
import { PayoutDisplay } from './ui/PayoutDisplay';
import { CashoutButton } from './ui/CashoutButton';
import { SpinButton } from './ui/SpinButton';
import { TurboToggle } from './ui/TurboToggle';
import { Modal } from './ui/Modal';
import { COLORS } from './config/colors';
import { INDICATOR_ANGLE_RAD, RING_MULTIPLIERS, RING_WIDTH, STATIC_RING_RADII, type RingColor } from './config/drumConfig';
import { FRUIT_ASSETS } from './config/fruitAssets';
import { getLang } from './utils/storage';
import { getText } from './i18n';
import type { Lang } from './i18n';
import { easeOutCubic } from './utils/math';
import { SoundManager } from './audio/SoundManager';
import { MUSIC, SFX } from './audio/soundConfig';
import { CUT_SOUND_DELAY_MS, JUICE_SOUND_DELAY_MS } from './rings/VerticalReel';

const DESIGN_WIDTH = 1920;
const DESIGN_HEIGHT = 1080;
/** Портрет для мобилок: ширина < высоты */
const DESIGN_WIDTH_MOBILE = 1080;
const DESIGN_HEIGHT_MOBILE = 1920;

function isMobileLayout(w: number, h: number): boolean {
  return w < h;
}

class App {
  private app!: PIXI.Application;
  private game!: Game;
  private header!: Header;
  private footer!: Footer;
  private betControl!: BetControl;
  private payoutDisplay!: PayoutDisplay;
  private cashoutBtn!: CashoutButton;
  private spinBtn!: SpinButton;
  private turboToggle!: TurboToggle;
  private mobileButtonsContainer!: PIXI.Container;
  private helpModal!: Modal;
  private menuModal!: Modal;
  private bonusModal!: Modal;
  private backgroundContainer!: PIXI.Container;
  private backgroundSprite!: PIXI.Sprite;
  private backgroundOverlay!: PIXI.Graphics;
  private backgroundMask!: PIXI.Graphics;
  private gameCircleContainer!: PIXI.Container;
  private ringsContainer!: PIXI.Container;
  private uiContainer!: PIXI.Container;
  private loaderEl?: HTMLElement;
  private loaderBarEl?: HTMLElement;
  private loaderTextEl?: HTMLElement;
  private lang: Lang = getLang();
  private turboEnabled = false;
  private bonusAnimating = false;

  constructor() {
    this.init();
  }

  private async init(): Promise<void> {
    this.setupLoaderElements();
    this.setLoaderProgress(0, 'Loading assets');
    await this.preloadAssets();
    this.hideLoader();

    const audio = SoundManager.get();
    audio.init();
    const container = document.getElementById('game-container');
    const containerWidth = container?.clientWidth ?? window.innerWidth;
    const containerHeight = container?.clientHeight ?? window.innerHeight;

    this.app = new PIXI.Application({
      width: containerWidth,
      height: containerHeight,
      backgroundColor: COLORS.backgroundDark,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    });

    if (container && this.app.view) {
      container.appendChild(this.app.view as HTMLCanvasElement);
    }

    this.setupContainers();
    this.setupBackground();
    this.setupRings();
    this.setupUI();
    this.setupModals();
    this.setupGame();
    this.setupResize();
    this.setupKeyboard();
    this.startGameLoop();
  }

  private setupLoaderElements(): void {
    this.loaderEl = document.getElementById('loader') ?? undefined;
    this.loaderBarEl = document.getElementById('loader-bar') ?? undefined;
    this.loaderTextEl = document.getElementById('loader-text') ?? undefined;
  }

  private setLoaderProgress(progress: number, label?: string): void {
    if (!this.loaderEl) return;
    const clamped = Math.max(0, Math.min(1, progress));
    const percent = Math.round(clamped * 100);
    if (this.loaderBarEl) this.loaderBarEl.style.width = `${percent}%`;
    if (this.loaderTextEl) {
      this.loaderTextEl.textContent = label ? `${label} ${percent}%` : `${percent}%`;
    }
  }

  private hideLoader(): void {
    if (!this.loaderEl) return;
    this.loaderEl.classList.add('loader--hidden');
    window.setTimeout(() => this.loaderEl?.remove(), 800);
  }

  private async preloadAssets(): Promise<void> {
    const lowPerformanceAudio = SoundManager.isLikelyMobileOrLowPerformanceDevice();
    const imageAssets = [
      '/img/background.webp',
      '/img/hold-to%20spin.webp',
      '/img/button-center.svg',
      ...Object.values(FRUIT_ASSETS),
    ];
    const audioAssets = SoundManager.get().isForcedMuted() || lowPerformanceAudio
      ? []
      : [
        ...Object.values(SFX).map((def) => def.src),
        ...Object.values(MUSIC).map((def) => def.src),
      ];
    const uniqueImages = Array.from(new Set(imageAssets));
    const uniqueAudio = Array.from(new Set(audioAssets));
    const total = Math.max(1, uniqueImages.length + uniqueAudio.length);
    let loaded = 0;
    const bump = (label?: string): void => {
      loaded += 1;
      this.setLoaderProgress(loaded / total, label);
    };

    await Promise.all([
      ...uniqueImages.map(async (url) => {
        try {
          await PIXI.Assets.load(url);
        } catch (err) {
          console.warn(`[preload] image failed: ${url}`, err);
        } finally {
          bump('Loading assets');
        }
      }),
      ...uniqueAudio.map(async (url) => {
        try {
          await this.loadAudio(url);
        } catch (err) {
          console.warn(`[preload] audio failed: ${url}`, err);
        } finally {
          bump('Loading assets');
        }
      }),
    ]);
  }

  private loadAudio(url: string): Promise<void> {
    return new Promise((resolve) => {
      const audio = new Audio();
      let settled = false;
      const finish = (): void => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve();
      };
      const cleanup = (): void => {
        audio.removeEventListener('canplaythrough', finish);
        audio.removeEventListener('loadeddata', finish);
        audio.removeEventListener('error', finish);
      };
      audio.preload = 'auto';
      audio.src = url;
      audio.addEventListener('canplaythrough', finish, { once: true });
      audio.addEventListener('loadeddata', finish, { once: true });
      audio.addEventListener('error', finish, { once: true });
      audio.load();
      window.setTimeout(finish, 6000);
    });
  }

  private setupContainers(): void {
    this.backgroundContainer = new PIXI.Container();
    this.app.stage.addChild(this.backgroundContainer);

    const gameCircleRadius = 420;
    this.gameCircleContainer = new PIXI.Container();
    this.gameCircleContainer.x = DESIGN_WIDTH / 2;
    this.gameCircleContainer.y = DESIGN_HEIGHT / 2;

    const circleBg = this.createGameCircleBackground(gameCircleRadius);
    this.gameCircleContainer.addChild(circleBg);

    this.ringsContainer = new PIXI.Container();
    this.gameCircleContainer.addChild(this.ringsContainer);

    this.app.stage.addChild(this.gameCircleContainer);

    this.uiContainer = new PIXI.Container();
    this.app.stage.addChild(this.uiContainer);
  }

  private createGameCircleBackground(radius: number): PIXI.Container {
    const size = radius * 2;
    const cx = radius;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cx, radius - 1, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    // Более яркий фон круга — светлее, с объёмом
    const angle1 = (318.21 * Math.PI) / 180;
    const x1 = cx + Math.cos(angle1) * radius * 2;
    const y1 = cx + Math.sin(angle1) * radius * 2;
    const grad1 = ctx.createLinearGradient(cx, cx, x1, y1);
    grad1.addColorStop(0.14, '#252a35');
    grad1.addColorStop(0.78, '#141820');
    ctx.fillStyle = grad1;
    ctx.fillRect(0, 0, size, size);

    const grad2 = ctx.createLinearGradient(0, size, size, 0);
    grad2.addColorStop(0.13, '#1e222c');
    grad2.addColorStop(0.87, '#202536');
    ctx.globalAlpha = 0.65;
    ctx.fillStyle = grad2;
    ctx.fillRect(0, 0, size, size);
    ctx.globalAlpha = 1;

    const grad3 = ctx.createLinearGradient(0, 0, size, size);
    grad3.addColorStop(0.15, '#5a6278');
    grad3.addColorStop(0.65, '#181c28');
    ctx.globalAlpha = 0.45;
    ctx.fillStyle = grad3;
    ctx.fillRect(0, 0, size, size);
    ctx.globalAlpha = 1;

    // Тень по краю — глубина
    const darkRadial = ctx.createRadialGradient(
      cx + 50, cx + 50, 0,
      cx + 50, cx + 50, radius * 1.2
    );
    darkRadial.addColorStop(0, 'transparent');
    darkRadial.addColorStop(0.5, 'transparent');
    darkRadial.addColorStop(1, 'rgba(8, 10, 18, 0.55)');
    ctx.fillStyle = darkRadial;
    ctx.fillRect(0, 0, size, size);

    // Подсветка — объём
    const lightRadial = ctx.createRadialGradient(
      cx - 50, cx - 50, 0,
      cx - 50, cx - 50, radius * 1.1
    );
    lightRadial.addColorStop(0, 'rgba(70, 78, 95, 0.5)');
    lightRadial.addColorStop(0.4, 'rgba(50, 56, 70, 0.2)');
    lightRadial.addColorStop(1, 'transparent');
    ctx.fillStyle = lightRadial;
    ctx.fillRect(0, 0, size, size);

    ctx.restore();

    const texture = PIXI.Texture.from(canvas);
    const sprite = new PIXI.Sprite(texture);
    sprite.anchor.set(0.5);
    sprite.x = 0;
    sprite.y = 0;

    const container = new PIXI.Container();
    container.addChild(sprite);

    // Яркая обводка по границе круга — колесо чётко отделено от фона
    const rim = new PIXI.Graphics();
    rim.lineStyle(3, 0xa0b0c8, 0.95);
    rim.drawCircle(0, 0, radius - 1);
    rim.lineStyle(0);
    container.addChild(rim);

    return container;
  }

  private setupBackground(): void {
    const texture = PIXI.Texture.from('/img/background.webp');
    this.backgroundSprite = new PIXI.Sprite(texture);
    this.backgroundSprite.width = DESIGN_WIDTH;
    this.backgroundSprite.height = DESIGN_HEIGHT;
    this.backgroundContainer.addChild(this.backgroundSprite);

    this.backgroundOverlay = new PIXI.Graphics();
    this.backgroundOverlay.beginFill(0x000000, 0.35);
    this.backgroundOverlay.drawRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);
    this.backgroundOverlay.endFill();
    this.backgroundContainer.addChild(this.backgroundOverlay);

    this.backgroundMask = new PIXI.Graphics();
    this.backgroundMask.beginFill(0xffffff);
    this.backgroundMask.drawRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);
    this.backgroundMask.endFill();
    this.backgroundContainer.addChild(this.backgroundMask);
    this.backgroundContainer.mask = this.backgroundMask;
  }

  private setupRings(): void {
    this.game = new Game(this.ringsContainer);
  }

  private setupUI(): void {
    this.header = new Header();
    this.header.y = 0;
    this.header.resize(DESIGN_WIDTH);
    this.uiContainer.addChild(this.header);

    this.footer = new Footer();
    this.footer.y = DESIGN_HEIGHT - 40;
    this.footer.resize(DESIGN_WIDTH);
    this.uiContainer.addChild(this.footer);

    const rightPanelWidth = 360;
    const rightPanelX = DESIGN_WIDTH - rightPanelWidth - 60;
    const rightPanelCenterX = rightPanelX + rightPanelWidth / 2;
    const rightPanelCenterY = DESIGN_HEIGHT / 2;

    this.betControl = new BetControl();
    const betOffsetX = 2;
    this.betControl.x = rightPanelCenterX - this.betControl.width / 2 - betOffsetX;
    this.betControl.y = rightPanelCenterY - 158;
    this.uiContainer.addChild(this.betControl);

    this.payoutDisplay = new PayoutDisplay();
    this.payoutDisplay.x = rightPanelCenterX;
    this.payoutDisplay.y = rightPanelCenterY + 118;
    this.uiContainer.addChild(this.payoutDisplay);

    const buttonsY = rightPanelCenterY;
    const spinBtnSize = 240;
    const cashoutBtnSize = 80;
    const gap = 10;

    this.spinBtn = new SpinButton();
    this.spinBtn.x = rightPanelCenterX;
    this.spinBtn.y = buttonsY;
    this.uiContainer.addChild(this.spinBtn);

    this.cashoutBtn = new CashoutButton();
    this.cashoutBtn.x = rightPanelCenterX - spinBtnSize / 2 - cashoutBtnSize - gap;
    this.cashoutBtn.y = buttonsY - cashoutBtnSize / 2;
    this.cashoutBtn.setEnabled(false);
    this.uiContainer.addChild(this.cashoutBtn);

    this.turboToggle = new TurboToggle();
    this.turboToggle.x = rightPanelCenterX + spinBtnSize / 2 + gap - 28;
    this.turboToggle.y = buttonsY - this.turboToggle.height / 2;
    this.uiContainer.addChild(this.turboToggle);

    this.mobileButtonsContainer = new PIXI.Container();
    this.updateLang();
  }

  private setupModals(): void {
    this.helpModal = new Modal({
      width: 600,
      height: 400,
      title: getText(this.lang, 'rules'),
    });
    this.helpModal.x = (DESIGN_WIDTH - 600) / 2;
    this.helpModal.y = (DESIGN_HEIGHT - 400) / 2;
    this.helpModal.visible = false;
    this.uiContainer.addChild(this.helpModal);

    const rulesText = new PIXI.Text(
      getText(this.lang, 'rulesText'),
      new TextStyle({
        fontFamily: '"Jost"',
        fontSize: 16,
        fill: COLORS.textPrimary,
        wordWrap: true,
        wordWrapWidth: 560,
      })
    );
    rulesText.x = 20;
    rulesText.y = 60;
    this.helpModal.getContent().addChild(rulesText);

    this.menuModal = new Modal({
      width: 500,
      height: 300,
      title: getText(this.lang, 'settings'),
    });
    this.menuModal.x = (DESIGN_WIDTH - 500) / 2;
    this.menuModal.y = (DESIGN_HEIGHT - 300) / 2;
    this.menuModal.visible = false;
    this.uiContainer.addChild(this.menuModal);

    this.bonusModal = new Modal({
      width: 560,
      height: 280,
      title: getText(this.lang, 'bonusRules'),
    });
    this.bonusModal.x = (DESIGN_WIDTH - 560) / 2;
    this.bonusModal.y = (DESIGN_HEIGHT - 280) / 2;
    this.bonusModal.visible = false;
    this.uiContainer.addChild(this.bonusModal);

    const bonusText = new PIXI.Text(
      getText(this.lang, 'bonusRulesText'),
      new TextStyle({
        fontFamily: '"Jost"',
        fontSize: 16,
        fill: COLORS.textPrimary,
        wordWrap: true,
        wordWrapWidth: 520,
      })
    );
    bonusText.x = 20;
    bonusText.y = 20;
    this.bonusModal.getContent().addChild(bonusText);

    this.game.setBonusInfoCallback(() => this.bonusModal.show());
  }

  private setupGame(): void {
    this.betControl.setOnChange((bet) => {
      SoundManager.get().playSfx('bet_change');
      this.game.setBet(bet);
    });

    this.spinBtn.on('pointerdown', () => {
      SoundManager.get().playSfx('ui_click');
      if (this.bonusAnimating) return;
      const state = this.game.getState();
      if (state.type === 'SPINNING') {
        this.game.requestStopSpin();
      } else if (this.game.canSpin()) {
        this.game.startSpin(this.turboEnabled);
      }
    });

    this.cashoutBtn.on('pointerdown', () => {
      SoundManager.get().playSfx('cashout');
      this.game.cashout();
    });

    this.turboToggle.setOnToggle((active) => {
      this.turboEnabled = active;
      SoundManager.get().playSfx(active ? 'turbo_on' : 'turbo_off');
    });

    this.header.setOnHelpClick(() => {
      SoundManager.get().playSfx('ui_click');
      this.helpModal.show();
    });

    this.header.setOnMenuClick(() => {
      SoundManager.get().playSfx('ui_click');
      this.menuModal.show();
    });

    this.game.on('state_changed', () => {
      this.updateUI();
    });

    let lastSpinId = -1;
    this.game.on('spin_start', (event) => {
      const sid = event.spinId ?? -1;
      if (sid !== -1 && sid !== lastSpinId) {
        lastSpinId = sid;
        SoundManager.get().playSfx('spin_start');
      }
    });

    this.game.on('spin_stop', () => {
      SoundManager.get().playSfx('spin_stop');
    });

    this.game.on('reel_stop', () => {
      SoundManager.get().playSfx('reel_stop');
    });

    this.game.on('segment_hit', (event) => {
      const seg = event.segment;
      if (seg?.type === 'skull') SoundManager.get().playSfx('skull_hit');
      if (seg?.type === 'gray') SoundManager.get().playSfx('gray_hit');
    });

    this.game.on('fruit_cut', () => {
      setTimeout(() => SoundManager.get().playSfx('fruit_cut'), CUT_SOUND_DELAY_MS);
      setTimeout(() => SoundManager.get().playSfx('juice_splash'), JUICE_SOUND_DELAY_MS);
    });

    this.game.on('sector_fill', () => {
      SoundManager.get().playSfx('sector_fill');
    });

    this.game.on('sector_drain', () => {
      SoundManager.get().playSfx('sector_drain');
    });

    this.game.on('balance_changed', () => {
      this.header.setBalance(this.game.getBalance());
    });

    this.game.on('payout_changed', () => {
      this.payoutDisplay.setPayout(this.game.getPayout());
    });

    this.game.on('bet_changed', () => {
      this.betControl.setBet(this.game.getBet());
    });

    this.game.on('bonus_won', (event) => {
      const mult = event.bonusMultiplier ?? 0;
      if (mult > 0) {
        SoundManager.get().playSfx('bonus_win');
        this.bonusAnimating = true;
        this.updateUI();
        this.animateBonusWheel(mult, () => {
          const amount = this.game.getBet() * mult;
          this.animateBonusCashoutFromCenter(amount, () => {
            this.game.finalizeRedBonusReset();
            this.bonusAnimating = false;
            this.updateUI();
          });
        });
      }
    });

    this.game.on('instant_bonus_cashout', (event) => {
      const amount = event.bonusCashout ?? 0;
      const color = event.bonusCashoutColor;
      const index = event.bonusCashoutIndex ?? 0;
      if (amount > 0 && color) {
        SoundManager.get().playSfx('instant_cashout');
        this.animateInstantCashout(amount, color, index);
      }
    });

    this.header.setBalance(this.game.getBalance());
    this.payoutDisplay.setPayout(0);
  }

  private updateUI(): void {
    const state = this.game.getState();
    // Спин разрешён только когда игра в IDLE и кольца не анимируют (game.canSpin() учитывает isAnimating())
    const canSpin = this.game.canSpin() && !this.bonusAnimating;
    const canCashout = state.type === 'IDLE' && state.payout > 0 && !this.bonusAnimating;

    this.spinBtn.setEnabled(canSpin);
    this.cashoutBtn.setEnabled(canCashout);
    this.betControl.setEnabled(canSpin);
    this.turboToggle.setEnabled(canSpin);
    this.payoutDisplay.setPayout(this.game.getPayout());
  }

  /** Показать сообщение о выигрыше бонуса (BONUS x200!) на 2 секунды */
  // private showBonusMessage(multiplier: number): void {
  //   const msg = new PIXI.Text(`BONUS x${multiplier}!`, new TextStyle({
  //     fontFamily: '"Jost"',
  //     fontSize: 64,
  //     fill: 0xffd700,
  //     fontWeight: 'bold',
  //   }));
  //   msg.anchor.set(0.5);
  //   msg.x = DESIGN_WIDTH / 2;
  //   msg.y = DESIGN_HEIGHT / 2;
  //   msg.alpha = 0;
  //   this.uiContainer.addChild(msg);
  //   const fadeInS = 0.2;
  //   const holdS = 2;
  //   const fadeOutS = 0.3;
  //   const start = performance.now();
  //   const tick = () => {
  //     const t = (performance.now() - start) / 1000;
  //     if (t < fadeInS) {
  //       msg.alpha = t / fadeInS;
  //     } else if (t < fadeInS + holdS) {
  //       msg.alpha = 1;
  //     } else if (t < fadeInS + holdS + fadeOutS) {
  //       msg.alpha = 1 - (t - fadeInS - holdS) / fadeOutS;
  //     } else {
  //       this.uiContainer.removeChild(msg);
  //       this.app.ticker.remove(tick);
  //       return;
  //     }
  //   };
  //   this.app.ticker.add(tick);
  // }

  private updateLang(): void {
    this.header.setLang(this.lang);
    this.betControl.setLang(this.lang);
    this.payoutDisplay.setLang(this.lang);
    this.cashoutBtn.setLang(this.lang);
    this.spinBtn.setLang(this.lang);
  }

  private animateInstantCashout(amount: number, color: RingColor, segmentIndex: number): void {
    const radius = STATIC_RING_RADII[color] - RING_WIDTH / 2;
    const sectorCount = RING_MULTIPLIERS[color].length;
    const angleStep = (2 * Math.PI) / sectorCount;
    const angle = INDICATOR_ANGLE_RAD + (segmentIndex + 0.5) * angleStep;
    const fromX = this.gameCircleContainer.x + Math.cos(angle) * radius;
    const fromY = this.gameCircleContainer.y + Math.sin(angle) * radius;
    const target = this.header.getBalanceTarget();

    const text = new PIXI.Text(`+$${amount.toFixed(2)}`, new TextStyle({
      fontFamily: '"Jost"',
      fontSize: 20,
      fill: COLORS.gold,
      fontStyle: 'italic',
      fontWeight: 'bold',
    }));
    text.anchor.set(0.5);
    text.x = fromX;
    text.y = fromY;
    this.uiContainer.addChild(text);

    const durationMs = 700;
    const start = performance.now();
    const tick = () => {
      const t = Math.min(1, (performance.now() - start) / durationMs);
      const eased = easeOutCubic(t);
      text.x = fromX + (target.x - fromX) * eased;
      text.y = fromY + (target.y - fromY) * eased;
      const fadeStart = 0.6;
      if (t >= fadeStart) {
        text.alpha = 1 - (t - fadeStart) / (1 - fadeStart);
      }
      text.scale.set(1 - 0.1 * eased);
      if (t >= 1) {
        this.uiContainer.removeChild(text);
        this.app.ticker.remove(tick);
      }
    };
    this.app.ticker.add(tick);
  }

  private setupResize(): void {
    const onResize = (): void => {
      const container = document.getElementById('game-container');
      const w = container?.clientWidth ?? window.innerWidth;
      const h = container?.clientHeight ?? window.innerHeight;
      this.app.renderer.resize(w, h);

      const mobile = isMobileLayout(w, h);
      const designW = mobile ? DESIGN_WIDTH_MOBILE : DESIGN_WIDTH;
      const designH = mobile ? DESIGN_HEIGHT_MOBILE : DESIGN_HEIGHT;
      const scale = Math.min(w / designW, h / designH);
      this.app.stage.scale.set(scale);
      this.app.stage.x = (w - designW * scale) / 2;
      this.app.stage.y = (h - designH * scale) / 2;

      if (mobile) {
        const tex = this.backgroundSprite.texture;
        const tw = tex?.width ?? designW;
        const th = tex?.height ?? designH;
        const coverScale = Math.max(designW / tw, designH / th);
        this.backgroundSprite.anchor.set(0.5);
        this.backgroundSprite.x = designW / 2;
        this.backgroundSprite.y = designH / 2;
        this.backgroundSprite.width = tw * coverScale;
        this.backgroundSprite.height = th * coverScale;
      } else {
        this.backgroundSprite.anchor.set(0, 0);
        this.backgroundSprite.x = 0;
        this.backgroundSprite.y = 0;
        this.backgroundSprite.width = designW;
        this.backgroundSprite.height = designH;
      }
      this.backgroundMask.clear();
      this.backgroundMask.beginFill(0xffffff);
      this.backgroundMask.drawRect(0, 0, designW, designH);
      this.backgroundMask.endFill();
      this.backgroundOverlay.clear();
      this.backgroundOverlay.beginFill(0x000000, 0.35);
      this.backgroundOverlay.drawRect(0, 0, designW, designH);
      this.backgroundOverlay.endFill();

      this.layoutUI(mobile, designW, designH);
      this.header.resize(designW, mobile);
      this.footer.resize(designW);
    };
    window.addEventListener('resize', onResize);
    onResize();
  }

  /** Раскладка: десктоп — круг по центру, панель справа; мобилка — круг сверху, кнопки снизу */
  private layoutUI(mobile: boolean, designW: number, designH: number): void {
    const rightPanelWidth = 360;
    const spinBtnSize = 240;
    const cashoutBtnSize = 80;
    const gap = 10;

    if (mobile) {
      // Круг в верхней части по центру
      const circleCenterY = 580;
      this.gameCircleContainer.x = designW / 2;
      this.gameCircleContainer.y = circleCenterY;

      this.header.y = 0;
      this.footer.y = designH - 40;

      const centerX = designW / 2;
      const buttonsAreaY = designH - 520;

      // Блок кнопок в контейнере с масштабом 2×
      if (!this.mobileButtonsContainer.parent) {
        [this.betControl, this.payoutDisplay, this.spinBtn, this.cashoutBtn, this.turboToggle].forEach((el) => {
          el.parent?.removeChild(el);
          this.mobileButtonsContainer.addChild(el);
        });
        this.uiContainer.addChild(this.mobileButtonsContainer);
      }
      this.mobileButtonsContainer.scale.set(2);
      this.mobileButtonsContainer.x = centerX;
      this.mobileButtonsContainer.y = buttonsAreaY;
      this.mobileButtonsContainer.pivot.set(0, 0);

      this.betControl.x = -this.betControl.width / 2 - 2;
      this.betControl.y = -158;
      this.payoutDisplay.x = 0;
      this.payoutDisplay.y = 118;
      this.spinBtn.x = 0;
      this.spinBtn.y = 0;
      this.cashoutBtn.x = -spinBtnSize / 2 - cashoutBtnSize - gap;
      this.cashoutBtn.y = -cashoutBtnSize / 2;
      this.turboToggle.x = spinBtnSize / 2 + gap - 28;
      this.turboToggle.y = -this.turboToggle.height / 2;
    } else {
      if (this.mobileButtonsContainer.parent) {
        this.uiContainer.removeChild(this.mobileButtonsContainer);
        [this.betControl, this.payoutDisplay, this.spinBtn, this.cashoutBtn, this.turboToggle].forEach((el) => {
          this.mobileButtonsContainer.removeChild(el);
          this.uiContainer.addChild(el);
        });
      }
      this.gameCircleContainer.x = designW / 2;
      this.gameCircleContainer.y = designH / 2;
      this.header.y = 0;
      this.footer.y = designH - 40;

      const rightPanelX = designW - rightPanelWidth - 60;
      const rightPanelCenterX = rightPanelX + rightPanelWidth / 2;
      const rightPanelCenterY = designH / 2;

      this.betControl.x = rightPanelCenterX - this.betControl.width / 2 - 2;
      this.betControl.y = rightPanelCenterY - 158;
      this.payoutDisplay.x = rightPanelCenterX;
      this.payoutDisplay.y = rightPanelCenterY + 118;
      this.spinBtn.x = rightPanelCenterX;
      this.spinBtn.y = rightPanelCenterY;
      this.cashoutBtn.x = rightPanelCenterX - spinBtnSize / 2 - cashoutBtnSize - gap;
      this.cashoutBtn.y = rightPanelCenterY - cashoutBtnSize / 2;
      this.turboToggle.x = rightPanelCenterX + spinBtnSize / 2 + gap - 28;
      this.turboToggle.y = rightPanelCenterY - this.turboToggle.height / 2;
    }

    this.helpModal.x = (designW - 600) / 2;
    this.helpModal.y = (designH - 400) / 2;
    this.menuModal.x = (designW - 500) / 2;
    this.menuModal.y = (designH - 300) / 2;
    this.bonusModal.x = (designW - 560) / 2;
    this.bonusModal.y = (designH - 280) / 2;
  }

  private setupKeyboard(): void {
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (this.bonusAnimating) return;
        const state = this.game.getState();
        if (state.type === 'SPINNING') {
          this.game.requestStopSpin();
        } else if (this.game.canSpin()) {
          this.game.startSpin(this.turboEnabled);
        }
      }
    });
  }

  private animateBonusWheel(multiplier: number, onComplete: () => void): void {
    const values = [100, 200, 300, 400, 500];
    const baseIndex = Math.max(0, values.indexOf(multiplier));
    const itemHeight = 40;
    const repeats = 3;
    // const totalItems = values.length * repeats;
    const displayIndex = baseIndex + values.length;
    const targetY = -displayIndex * itemHeight;
    const startY = targetY - values.length * itemHeight * 2;

    const panel = new PIXI.Container();
    panel.x = this.gameCircleContainer.x;
    panel.y = this.gameCircleContainer.y - 10;

    const bg = new PIXI.Graphics();
    bg.beginFill(0x0b0b12, 0.9);
    bg.lineStyle(2, COLORS.purple, 0.8);
    bg.drawRoundedRect(-70, -40, 140, 80, 20);
    bg.endFill();
    panel.addChild(bg);

    const mask = new PIXI.Graphics();
    mask.beginFill(0xffffff);
    mask.drawRoundedRect(-60, -24, 120, 48, 14);
    mask.endFill();
    panel.addChild(mask);

    const reelContainer = new PIXI.Container();
    reelContainer.mask = mask;
    for (let r = 0; r < repeats; r++) {
      for (let i = 0; i < values.length; i++) {
        const text = new PIXI.Text(`x${values[i]}`, new TextStyle({
          fontFamily: '"Jost"',
          fontSize: 28,
          fill: COLORS.gold,
          fontWeight: 'bold',
        }));
        text.anchor.set(0.5);
        text.x = 0;
        text.y = (r * values.length + i) * itemHeight;
        reelContainer.addChild(text);
      }
    }
    reelContainer.y = startY;
    panel.addChild(reelContainer);

    this.uiContainer.addChild(panel);

    const durationMs = 1400;
    const start = performance.now();
    const tick = () => {
      const t = Math.min(1, (performance.now() - start) / durationMs);
      const eased = easeOutCubic(t);
      reelContainer.y = startY + (targetY - startY) * eased;
      if (t >= 1) {
        this.app.ticker.remove(tick);
        setTimeout(() => {
          this.uiContainer.removeChild(panel);
          onComplete();
        }, 350);
      }
    };
    this.app.ticker.add(tick);
  }

  private animateBonusCashoutFromCenter(amount: number, onComplete: () => void): void {
    const fromX = this.gameCircleContainer.x;
    const fromY = this.gameCircleContainer.y;
    const target = this.header.getBalanceTarget();

    const text = new PIXI.Text(`+$${amount.toFixed(2)}`, new TextStyle({
      fontFamily: '"Jost"',
      fontSize: 22,
      fill: COLORS.gold,
      fontStyle: 'italic',
      fontWeight: 'bold',
    }));
    text.anchor.set(0.5);
    text.x = fromX;
    text.y = fromY;
    this.uiContainer.addChild(text);

    const durationMs = 800;
    const start = performance.now();
    const tick = () => {
      const t = Math.min(1, (performance.now() - start) / durationMs);
      const eased = easeOutCubic(t);
      text.x = fromX + (target.x - fromX) * eased;
      text.y = fromY + (target.y - fromY) * eased;
      if (t >= 0.6) {
        text.alpha = 1 - (t - 0.6) / 0.4;
      }
      if (t >= 1) {
        this.uiContainer.removeChild(text);
        this.app.ticker.remove(tick);
        onComplete();
      }
    };
    this.app.ticker.add(tick);
  }

  private startGameLoop(): void {
    this.app.ticker.add(() => {
      const delta = this.app.ticker.deltaTime;
      this.game.update(delta);
      this.spinBtn.update(delta);
      // Обновляем кнопку спина каждый кадр: спин запрещён пока кольца анимируют (заливка/слив)
      this.updateUI();
    });
  }
}

new App();
